import { PayloadAction, createAction, createSlice } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import { Preferences } from "../../profile/objects/preferences";
import { EMPTY_SALES_RECEIPT_LIST_CONFIGURATION, EMPTY_SALES_RECEIPT_LIST_CRITERIA, EMPTY_SALES_RECEIPT_INVOICE_LIST_CRITERIA, EMPTY_SALES_RECEIPT_CLIENT_LIST_CONFIGURATION, EMPTY_SALES_RECEIPT_CLIENT_LIST_CRITERIA, EMPTY_SALES_RECEIPT_INVOICE_LIST_CONFIGURATION, EMPTY_SALES_BILL_PARAMETERS, EMPTY_SALES_RECEIPT_PAID_LIST_CONFIGURATION, EMPTY_SALES_RECEIPT_PAID_LIST_CRITERIA, EMPTY_SALES_STATS, EMPTY_THIRD_PARTY_LIST_CONFIGURATION, EMPTY_THIRD_PARTY_LIST_CRITERIA, EMPTY_SALES_RECEIPT } from "../constant/salesReceiptConstant";
import { SalesReceipt } from "../objects/salesReceipt";
import { SalesReceiptListCriteria } from "../objects/salesReceiptListCriteria";
import { SalesReceiptState } from "../objects/salesReceiptState";
import { setSalesReceiptForecastPreference } from "../utils/setSalesReceiptForecastPreference";
import { Organisation } from "../../organisation/objects/organisation";
import { CustomProvider } from "../../customProvider/objects/customProvider";
import { setSalesReceiptClientPreference } from "../utils/setSalesReceiptClientPreference";
import { setSalesReceiptInvoicePreference } from "../utils/setSalesReceiptInvoicePreference";
import { SaleBillParameters } from "../objects/saleBill";
import { Column } from "../objects/column";
import { Currency } from "../../currency/objects/currency";
import { SalesReceiptClientListCriteria } from "../objects/salesReceiptClientListCriteria";
import { setSalesReceiptPaidPreference } from "../utils/setSalesReceiptPaidPreference";
import { BillPayment } from "../objects/billPayment";
import { SalesReceiptPaidListCriteria } from "../objects/salesReceiptPaidListCriteria";
import { SalesStats } from "../objects/salesStats";
import { setThirdPartyPreference } from "../utils/setThirdPartyPreference";
import { ThirdParty } from "../objects/thirdParty";
import { SalesReceiptThirdPartyListCriteria } from "../objects/salesReceiptThirdPartyList";
import { SalesReceiptInvoiceListCriteria } from "../objects/salesReceiptInvoiceListCriteria";


const initialState: SalesReceiptState = {


    forecast_list: [],
    forecast_list_loaded: false,
    selected_forecast: [],
    forecast_refresh_list: false,
    forecast_list_configuration: EMPTY_SALES_RECEIPT_LIST_CONFIGURATION,
    forecast_list_loading: false,
    forecast_list_count: 0,
    forecast_list_criteria: EMPTY_SALES_RECEIPT_LIST_CRITERIA,
    forecast_is_creating: false,
    forecast: null,
    forecast_is_grouping: false,
    forecast_grouping_list_open: false,

    client_list: [],
    client_list_loaded: false,
    selected_client: [],
    client_refresh_list: false,
    client_list_configuration: EMPTY_SALES_RECEIPT_CLIENT_LIST_CONFIGURATION,
    client_list_loading: false,
    client_list_count: 0,
    client_list_criteria: EMPTY_SALES_RECEIPT_CLIENT_LIST_CRITERIA,
    client_to_pay: null,

    invoice_list: [],
    invoice_list_loaded: false,
    selected_invoice: [],
    collect_invoice: null,
    invoice_refresh_list: false,
    invoice_list_configuration: EMPTY_SALES_RECEIPT_INVOICE_LIST_CONFIGURATION,
    invoice_list_loading: false,
    invoice_list_count: 0,
    invoice_list_criteria: EMPTY_SALES_RECEIPT_INVOICE_LIST_CRITERIA,
    invoice_is_creating: false,
    invoice: null,

    paid_list: [],
    paid_list_loaded: false,
    selected_paid: [],
    paid_refresh_list: false,
    paid_list_configuration: EMPTY_SALES_RECEIPT_PAID_LIST_CONFIGURATION,
    paid_list_loading: false,
    paid_list_count: 0,
    paid_list_criteria: EMPTY_SALES_RECEIPT_PAID_LIST_CRITERIA,
    paid_is_creating: false,
    paid: null,

    third_party_list: [],
    third_party_list_loaded: false,
    selected_third_party: [],
    third_party_refresh_list: false,
    third_party_list_configuration: EMPTY_THIRD_PARTY_LIST_CONFIGURATION,
    third_party_list_loading: false,
    third_party_list_count: 0,
    third_party_list_criteria: EMPTY_THIRD_PARTY_LIST_CRITERIA,
    third_party_is_creating: false,
    third_party: null,

    sale_bill_parameters: EMPTY_SALES_BILL_PARAMETERS,
    sale_bill_parameters_loaded: false,
    sale_bill_parameters_is_saved: true,

    sales_receipt: null,

    company: null,

    currencies: [],
    payment_types: [],
    prefered_payment_type: "RM RGT1",
    payment_types_loaded: false,
    sales_stats: EMPTY_SALES_STATS,
    empty_sales_receipt: EMPTY_SALES_RECEIPT,

    first_preference_loaded: false,

};

export const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');

const setCompanyReducer = (state: SalesReceiptState, action: PayloadAction<Organisation | null>): SalesReceiptState => ({
    ...state, company: action.payload
});
const updateSalesReceiptReducer = (state: SalesReceiptState, action: PayloadAction<SalesReceipt | null>): SalesReceiptState => ({
    ...state, sales_receipt: action.payload
});

const setPreferenceReducer = (state: SalesReceiptState, action: PayloadAction<Preferences[]>): SalesReceiptState => {
    const { preferences, columns, order_by, order } = setSalesReceiptForecastPreference(action.payload, state.forecast_list_configuration.columns);
    const client_preference = setSalesReceiptClientPreference(action.payload, state.client_list_configuration.columns);
    const invoice_preference = setSalesReceiptInvoicePreference(action.payload, state.invoice_list_configuration.columns);
    const paid_preference = setSalesReceiptPaidPreference(action.payload, state.paid_list_configuration.columns);
    const third_party_preference = setThirdPartyPreference(action.payload, state.third_party_list_configuration.columns);
    return {
        ...state,
        first_preference_loaded: true,
        forecast_list_configuration: { ...state.forecast_list_configuration, preferences, columns },
        forecast_list_criteria: { ...state.forecast_list_criteria, order, order_by },
        client_list_configuration: { ...state.client_list_configuration, preferences: client_preference.preferences, columns: client_preference.columns },
        client_list_criteria: { ...state.client_list_criteria, order: client_preference.order, order_by: client_preference.order_by },
        invoice_list_configuration: { ...state.invoice_list_configuration, preferences: invoice_preference.preferences, columns: invoice_preference.columns },
        invoice_list_criteria: { ...state.invoice_list_criteria, order: invoice_preference.order, order_by: invoice_preference.order_by },
        paid_list_configuration: { ...state.paid_list_configuration, preferences: paid_preference.preferences, columns: paid_preference.columns },
        paid_list_criteria: { ...state.paid_list_criteria, order: paid_preference.order, order_by: paid_preference.order_by },
        third_party_list_configuration: { ...state.third_party_list_configuration, preferences: third_party_preference.preferences, columns: third_party_preference.columns },
        third_party_list_criteria: { ...state.third_party_list_criteria, order: third_party_preference.order, order_by: third_party_preference.order_by }
    };
};



//  ------------------------------ forecast ----------------------------------- //
const toggleForecastRefreshListReducer = (state: SalesReceiptState): SalesReceiptState => ({
    ...state, forecast_refresh_list: !state.forecast_refresh_list, forecast_list_loaded: false
});
const setSelectedForecastReducer = (state: SalesReceiptState, action: PayloadAction<SalesReceipt[]>): SalesReceiptState => ({
    ...state, selected_forecast: action.payload
});
const addForecastListConfigurationPreferenceReducer = (state: SalesReceiptState, action: PayloadAction<Preferences[]>): SalesReceiptState => ({
    ...state, forecast_list_configuration: { ...state.forecast_list_configuration, preferences: [...state.forecast_list_configuration.preferences, ...action.payload] }
});
const setForecastListConfigurationPreferenceReducer = (state: SalesReceiptState, action: PayloadAction<Preferences[]>): SalesReceiptState => ({
    ...state, forecast_list_configuration: { ...state.forecast_list_configuration, preferences: action.payload }
});
const updateForecastListReducer = (state: SalesReceiptState, action: PayloadAction<SalesReceipt[]>): SalesReceiptState => ({
    ...state, forecast_list: action.payload, forecast_list_loaded: true
});
const updateForecastListCriteriaReducer = (state: SalesReceiptState, action: PayloadAction<SalesReceiptListCriteria>): SalesReceiptState => ({
    ...state, forecast_list_criteria: action.payload
});
const updateForecastListLoadingReducer = (state: SalesReceiptState, action: PayloadAction<boolean>): SalesReceiptState => ({
    ...state, forecast_list_loading: action.payload
});
const updateForecastListCountReducer = (state: SalesReceiptState, action: PayloadAction<number>): SalesReceiptState => ({
    ...state, forecast_list_count: action.payload
});
const updateForecastIsCreatingReducer = (state: SalesReceiptState, action: PayloadAction<boolean>): SalesReceiptState => ({
    ...state, forecast_is_creating: action.payload
});
const toggleColumnReducer = (state: SalesReceiptState, action: PayloadAction<number>): SalesReceiptState => {
    const columns = cloneDeep([...state.forecast_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, forecast_list_configuration: { ...state.forecast_list_configuration, columns } };
};
const setColumnsReducer = (state: SalesReceiptState, action: PayloadAction<Column[]>): SalesReceiptState => ({
    ...state, forecast_list_configuration: { ...state.forecast_list_configuration, columns: action.payload }
});
const setForecastReducer = (state: SalesReceiptState, action: PayloadAction<SalesReceipt | null>): SalesReceiptState => ({
    ...state, forecast:  action.payload ? { ...action.payload, invoice_product: action.payload.invoice_product.map((product) => ({ ...product, start_date: product.start_date ? new Date(product.start_date) : null })) } : null
});
const setForecastIsGroupingReducer = (state: SalesReceiptState, action: PayloadAction<boolean>): SalesReceiptState => ({
    ...state, forecast_is_grouping: action.payload
});
const setForecastGroupingListOpenReducer = (state: SalesReceiptState, action: PayloadAction<boolean>): SalesReceiptState => ({
    ...state, forecast_grouping_list_open: action.payload
});



//  ------------------------------ client ----------------------------------- //
const toggleClientRefreshListReducer = (state: SalesReceiptState): SalesReceiptState => ({
    ...state, client_refresh_list: !state.client_refresh_list, client_list_loaded: false
});

const setSelectedClientReducer = (state: SalesReceiptState, action: PayloadAction<CustomProvider[]>): SalesReceiptState => ({
    ...state, selected_client: action.payload
});

const addClientListConfigurationPreferenceReducer = (state: SalesReceiptState, action: PayloadAction<Preferences[]>): SalesReceiptState => ({
    ...state, client_list_configuration: { ...state.client_list_configuration, preferences: [...state.client_list_configuration.preferences, ...action.payload] }
});

const setClientListConfigurationPreferenceReducer = (state: SalesReceiptState, action: PayloadAction<Preferences[]>): SalesReceiptState => ({
    ...state, client_list_configuration: { ...state.client_list_configuration, preferences: action.payload }
});
const toggleColumnClientReducer = (state: SalesReceiptState, action: PayloadAction<number>): SalesReceiptState => {
    const columns = cloneDeep([...state.client_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, client_list_configuration: { ...state.client_list_configuration, columns } };
};

const updateClientListReducer = (state: SalesReceiptState, action: PayloadAction<CustomProvider[]>): SalesReceiptState => ({
    ...state, client_list: action.payload, client_list_loaded: true
});
const updateClientListCriteriaReducer = (state: SalesReceiptState, action: PayloadAction<SalesReceiptClientListCriteria>): SalesReceiptState => ({
    ...state, client_list_criteria: action.payload
});

const updateClientListLoadingReducer = (state: SalesReceiptState, action: PayloadAction<boolean>): SalesReceiptState => ({
    ...state, client_list_loading: action.payload
});
const updateClientListCountReducer = (state: SalesReceiptState, action: PayloadAction<number>): SalesReceiptState => ({
    ...state, client_list_count: action.payload
});
const setClientColumnsReducer = (state: SalesReceiptState, action: PayloadAction<Column[]>): SalesReceiptState => ({
    ...state, client_list_configuration: { ...state.client_list_configuration, columns: action.payload }
});
const setClientToPayReducer = (state: SalesReceiptState, action: PayloadAction<any>): SalesReceiptState => ({
    ...state, client_to_pay: action.payload
});




//  ------------------------------ invoice ----------------------------------- //
const toggleInvoiceRefreshListReducer = (state: SalesReceiptState): SalesReceiptState => ({
    ...state, invoice_refresh_list: !state.invoice_refresh_list, invoice_list_loaded: false
});
const setSelectedInvoiceReducer = (state: SalesReceiptState, action: PayloadAction<SalesReceipt[]>): SalesReceiptState => ({
    ...state, selected_invoice: action.payload
});
const addInvoiceListConfigurationPreferenceReducer = (state: SalesReceiptState, action: PayloadAction<Preferences[]>): SalesReceiptState => ({
    ...state, invoice_list_configuration: { ...state.invoice_list_configuration, preferences: [...state.invoice_list_configuration.preferences, ...action.payload] }
});
const setInvoiceListConfigurationPreferenceReducer = (state: SalesReceiptState, action: PayloadAction<Preferences[]>): SalesReceiptState => ({
    ...state, invoice_list_configuration: { ...state.invoice_list_configuration, preferences: action.payload }
});
const toggleColumnInvoiceReducer = (state: SalesReceiptState, action: PayloadAction<number>): SalesReceiptState => {
    const columns = cloneDeep([...state.invoice_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, invoice_list_configuration: { ...state.invoice_list_configuration, columns } };
};
const updateInvoiceListReducer = (state: SalesReceiptState, action: PayloadAction<SalesReceipt[]>): SalesReceiptState => ({
    ...state, invoice_list: action.payload, invoice_list_loaded: true
});
const updateInvoiceListCriteriaReducer = (state: SalesReceiptState, action: PayloadAction<SalesReceiptInvoiceListCriteria>): SalesReceiptState => ({
    ...state, invoice_list_criteria: action.payload
});
const updateInvoiceListLoadingReducer = (state: SalesReceiptState, action: PayloadAction<boolean>): SalesReceiptState => ({
    ...state, invoice_list_loading: action.payload
});
const updateInvoiceListCountReducer = (state: SalesReceiptState, action: PayloadAction<number>): SalesReceiptState => ({
    ...state, invoice_list_count: action.payload
});
const updateInvoiceIsCreatingReducer = (state: SalesReceiptState, action: PayloadAction<boolean>): SalesReceiptState => ({
    ...state, invoice_is_creating: action.payload
});
const updateSaleBillParametersReducer = (state: SalesReceiptState, action: PayloadAction<{ key: keyof SaleBillParameters, value: boolean }>): SalesReceiptState => ({
    ...state, sale_bill_parameters: { ...state.sale_bill_parameters, [action.payload.key]: action.payload.value }
});
const setBillParametersReducer = (state: SalesReceiptState, action: PayloadAction<SaleBillParameters>): SalesReceiptState => ({
    ...state, sale_bill_parameters: action.payload, sale_bill_parameters_loaded: true
});
const setBillParametersIsSavedReducer = (state: SalesReceiptState, action: PayloadAction<boolean>): SalesReceiptState => ({
    ...state, sale_bill_parameters_is_saved: action.payload
});
const setInvoiceColumnsReducer = (state: SalesReceiptState, action: PayloadAction<Column[]>): SalesReceiptState => ({
    ...state, invoice_list_configuration: { ...state.invoice_list_configuration, columns: action.payload }
});
const setInvoiceReducer = (state: SalesReceiptState, action: PayloadAction<SalesReceipt | null>): SalesReceiptState => ({
    ...state, invoice: action.payload ? { ...action.payload, invoice_product: action.payload.invoice_product.map((product) => ({ ...product, start_date: product.start_date ? new Date(product.start_date) : null })) } : null
});




//  ------------------------------ paid ----------------------------------- //
const togglePaidRefreshListReducer = (state: SalesReceiptState): SalesReceiptState => ({
    ...state, paid_refresh_list: !state.paid_refresh_list, paid_list_loaded: false
});
const setSelectedPaidReducer = (state: SalesReceiptState, action: PayloadAction<BillPayment[]>): SalesReceiptState => ({
    ...state, selected_paid: action.payload
});
const addPaidListConfigurationPreferenceReducer = (state: SalesReceiptState, action: PayloadAction<Preferences[]>): SalesReceiptState => ({
    ...state, paid_list_configuration: { ...state.paid_list_configuration, preferences: [...state.paid_list_configuration.preferences, ...action.payload] }
});
const setPaidListConfigurationPreferenceReducer = (state: SalesReceiptState, action: PayloadAction<Preferences[]>): SalesReceiptState => ({
    ...state, paid_list_configuration: { ...state.paid_list_configuration, preferences: action.payload }
});
const toggleColumnPaidReducer = (state: SalesReceiptState, action: PayloadAction<number>): SalesReceiptState => {
    const columns = cloneDeep([...state.paid_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, paid_list_configuration: { ...state.paid_list_configuration, columns } };
};
const updatePaidListReducer = (state: SalesReceiptState, action: PayloadAction<BillPayment[]>): SalesReceiptState => ({
    ...state, paid_list: action.payload, paid_list_loaded: true
});
const updatePaidListCriteriaReducer = (state: SalesReceiptState, action: PayloadAction<SalesReceiptPaidListCriteria>): SalesReceiptState => ({
    ...state, paid_list_criteria: action.payload
});
const updatePaidListLoadingReducer = (state: SalesReceiptState, action: PayloadAction<boolean>): SalesReceiptState => ({
    ...state, paid_list_loading: action.payload
});
const updatePaidListCountReducer = (state: SalesReceiptState, action: PayloadAction<number>): SalesReceiptState => ({
    ...state, paid_list_count: action.payload
});
const updatePaidIsCreatingReducer = (state: SalesReceiptState, action: PayloadAction<boolean>): SalesReceiptState => ({
    ...state, paid_is_creating: action.payload
});
const setPaidColumnsReducer = (state: SalesReceiptState, action: PayloadAction<Column[]>): SalesReceiptState => ({
    ...state, paid_list_configuration: { ...state.paid_list_configuration, columns: action.payload }
});
const setPaidReducer = (state: SalesReceiptState, action: PayloadAction<BillPayment | null>): SalesReceiptState => ({
    ...state, paid: action.payload
});
const setPaymentTypesReducer = (state: SalesReceiptState, action: PayloadAction<string[]>): SalesReceiptState => ({
    ...state, payment_types: action.payload, payment_types_loaded: true
});
const setPreferedPaymentTypeReducer = (state: SalesReceiptState, action: PayloadAction<string>): SalesReceiptState => ({
    ...state, prefered_payment_type: action.payload
});
const setCollectInvoiceReducer = (state: SalesReceiptState, action: PayloadAction<SalesReceipt | null>): SalesReceiptState => ({
    ...state, collect_invoice: action.payload
});



//  ------------------------------ third_party ----------------------------------- //
const toggleThirdPartyRefreshListReducer = (state: SalesReceiptState): SalesReceiptState => ({
    ...state, third_party_refresh_list: !state.third_party_refresh_list, third_party_list_loaded: false
});
const setSelectedThirdPartyReducer = (state: SalesReceiptState, action: PayloadAction<ThirdParty[]>): SalesReceiptState => ({
    ...state, selected_third_party: action.payload
});
const addThirdPartyListConfigurationPreferenceReducer = (state: SalesReceiptState, action: PayloadAction<Preferences[]>): SalesReceiptState => ({
    ...state, third_party_list_configuration: { ...state.third_party_list_configuration, preferences: [...state.third_party_list_configuration.preferences, ...action.payload] }
});
const setThirdPartyListConfigurationPreferenceReducer = (state: SalesReceiptState, action: PayloadAction<Preferences[]>): SalesReceiptState => ({
    ...state, third_party_list_configuration: { ...state.third_party_list_configuration, preferences: action.payload }
});
const toggleColumnThirdPartyReducer = (state: SalesReceiptState, action: PayloadAction<number>): SalesReceiptState => {
    const columns = cloneDeep([...state.third_party_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, third_party_list_configuration: { ...state.third_party_list_configuration, columns } };
};
const updateThirdPartyListReducer = (state: SalesReceiptState, action: PayloadAction<ThirdParty[]>): SalesReceiptState => ({
    ...state, third_party_list: action.payload, third_party_list_loaded: true
});
const updateThirdPartyListCriteriaReducer = (state: SalesReceiptState, action: PayloadAction<SalesReceiptThirdPartyListCriteria>): SalesReceiptState => ({
    ...state, third_party_list_criteria: action.payload
});
const updateThirdPartyListLoadingReducer = (state: SalesReceiptState, action: PayloadAction<boolean>): SalesReceiptState => ({
    ...state, third_party_list_loading: action.payload
});
const updateThirdPartyListCountReducer = (state: SalesReceiptState, action: PayloadAction<number>): SalesReceiptState => ({
    ...state, third_party_list_count: action.payload
});
const updateThirdPartyIsCreatingReducer = (state: SalesReceiptState, action: PayloadAction<boolean>): SalesReceiptState => ({
    ...state, third_party_is_creating: action.payload
});
const setThirdPartyColumnsReducer = (state: SalesReceiptState, action: PayloadAction<Column[]>): SalesReceiptState => ({
    ...state, third_party_list_configuration: { ...state.third_party_list_configuration, columns: action.payload }
});
const setThirdPartyReducer = (state: SalesReceiptState, action: PayloadAction<ThirdParty | null>): SalesReceiptState => ({
    ...state, third_party: action.payload
});




// --------------- currencies ----------------------- //
const setCurrenciesReducer = (state: SalesReceiptState, action: PayloadAction<Currency[]>): SalesReceiptState => ({
    ...state, currencies: action.payload
});

// ----------------- stats --------------------- //
const setSalesStatsReducer = (state: SalesReceiptState, action: PayloadAction<SalesStats>): SalesReceiptState => ({
    ...state, sales_stats: action.payload
});


const setEmptySalesReceiptReducer =  (state: SalesReceiptState, action: PayloadAction<SalesReceipt>): SalesReceiptState => ({
    ...state, empty_sales_receipt: action.payload
});


export const salesReceipt = createSlice({
    name: 'sales_receipt',
    initialState,
    reducers: {
        setCompany: setCompanyReducer,
        updateSalesReceipt: updateSalesReceiptReducer,

        setSelectedForecast: setSelectedForecastReducer,
        toggleForecastRefreshList: toggleForecastRefreshListReducer,
        addForecastListConfigurationPreference: addForecastListConfigurationPreferenceReducer,
        setForecastListConfigurationPreference: setForecastListConfigurationPreferenceReducer,
        updateForecastList: updateForecastListReducer,
        updateForecastListCriteria: updateForecastListCriteriaReducer,
        updateForecastListLoading: updateForecastListLoadingReducer,
        updateForecastListCount: updateForecastListCountReducer,
        updateForecastIsCreating: updateForecastIsCreatingReducer,
        setColumns: setColumnsReducer,
        toggleColumn: toggleColumnReducer,
        setForecast: setForecastReducer,
        setForecastIsGrouping: setForecastIsGroupingReducer,
        setForecastGroupingListOpen: setForecastGroupingListOpenReducer,

        setSelectedClient: setSelectedClientReducer,
        toggleClientRefreshList: toggleClientRefreshListReducer,
        addClientListConfigurationPreference: addClientListConfigurationPreferenceReducer,
        setClientListConfigurationPreference: setClientListConfigurationPreferenceReducer,
        updateClientList: updateClientListReducer,
        updateClientListCriteria: updateClientListCriteriaReducer,
        updateClientListLoading: updateClientListLoadingReducer,
        updateClientListCount: updateClientListCountReducer,
        toggleColumnClient: toggleColumnClientReducer,
        setClientColumns: setClientColumnsReducer,
        setClientToPay: setClientToPayReducer,

        setSelectedInvoice: setSelectedInvoiceReducer,
        toggleInvoiceRefreshList: toggleInvoiceRefreshListReducer,
        addInvoiceListConfigurationPreference: addInvoiceListConfigurationPreferenceReducer,
        setInvoiceListConfigurationPreference: setInvoiceListConfigurationPreferenceReducer,
        updateInvoiceList: updateInvoiceListReducer,
        updateInvoiceListCriteria: updateInvoiceListCriteriaReducer,
        updateInvoiceListLoading: updateInvoiceListLoadingReducer,
        updateInvoiceListCount: updateInvoiceListCountReducer,
        updateInvoiceIsCreating: updateInvoiceIsCreatingReducer,
        updateSaleBillParameters: updateSaleBillParametersReducer,
        setBillParameters: setBillParametersReducer,
        setBillParametersIsSaved: setBillParametersIsSavedReducer,
        toggleColumnInvoice: toggleColumnInvoiceReducer,
        setInvoiceColumns: setInvoiceColumnsReducer,
        setInvoice: setInvoiceReducer,

        setSelectedPaid: setSelectedPaidReducer,
        togglePaidRefreshList: togglePaidRefreshListReducer,
        addPaidListConfigurationPreference: addPaidListConfigurationPreferenceReducer,
        setPaidListConfigurationPreference: setPaidListConfigurationPreferenceReducer,
        updatePaidList: updatePaidListReducer,
        updatePaidListCriteria: updatePaidListCriteriaReducer,
        updatePaidListLoading: updatePaidListLoadingReducer,
        updatePaidListCount: updatePaidListCountReducer,
        updatePaidIsCreating: updatePaidIsCreatingReducer,
        toggleColumnPaid: toggleColumnPaidReducer,
        setPaidColumns: setPaidColumnsReducer,
        setPaid: setPaidReducer,

        setSelectedThirdParty: setSelectedThirdPartyReducer,
        toggleThirdPartyRefreshList: toggleThirdPartyRefreshListReducer,
        addThirdPartyListConfigurationPreference: addThirdPartyListConfigurationPreferenceReducer,
        setThirdPartyListConfigurationPreference: setThirdPartyListConfigurationPreferenceReducer,
        updateThirdPartyList: updateThirdPartyListReducer,
        updateThirdPartyListCriteria: updateThirdPartyListCriteriaReducer,
        updateThirdPartyListLoading: updateThirdPartyListLoadingReducer,
        updateThirdPartyListCount: updateThirdPartyListCountReducer,
        updateThirdPartyIsCreating: updateThirdPartyIsCreatingReducer,
        toggleColumnThirdParty: toggleColumnThirdPartyReducer,
        setThirdPartyColumns: setThirdPartyColumnsReducer,
        setThirdParty: setThirdPartyReducer,

        setCollectInvoice: setCollectInvoiceReducer,

        setCurrencies: setCurrenciesReducer,
        setPaymentTypes: setPaymentTypesReducer,
        setPreferedPaymentType: setPreferedPaymentTypeReducer,
        setSalesStats: setSalesStatsReducer,
        setEmptySalesReceipt: setEmptySalesReceiptReducer,
    },
    extraReducers: (builder) => {
        builder
            .addCase(setPreferences, setPreferenceReducer);
    }
});

export const {
    setCompany,
    setSelectedForecast,
    toggleForecastRefreshList,
    addForecastListConfigurationPreference,
    setForecastListConfigurationPreference,
    updateForecastList,
    updateForecastListCriteria,
    updateSalesReceipt,
    updateForecastListLoading,
    updateForecastListCount,
    updateForecastIsCreating,
    setColumns,
    toggleColumn,
    setForecast,
    setForecastIsGrouping,
    setForecastGroupingListOpen,

    setSelectedClient,
    toggleClientRefreshList,
    addClientListConfigurationPreference,
    setClientListConfigurationPreference,
    updateClientList,
    updateClientListCriteria,
    updateClientListLoading,
    updateClientListCount,
    toggleColumnClient,
    setClientColumns,
    setClientToPay,


    setSelectedInvoice,
    toggleInvoiceRefreshList,
    addInvoiceListConfigurationPreference,
    setInvoiceListConfigurationPreference,
    toggleColumnInvoice,
    updateInvoiceList,
    updateInvoiceListCriteria,
    updateInvoiceListLoading,
    updateInvoiceListCount,
    updateInvoiceIsCreating,
    setInvoiceColumns,
    setInvoice,

    updateSaleBillParameters,
    setBillParameters,
    setBillParametersIsSaved,

    setSelectedPaid,
    togglePaidRefreshList,
    addPaidListConfigurationPreference,
    setPaidListConfigurationPreference,
    toggleColumnPaid,
    updatePaidList,
    updatePaidListCriteria,
    updatePaidListLoading,
    updatePaidListCount,
    updatePaidIsCreating,
    setPaidColumns,
    setPaid,
    setCollectInvoice,

    setSelectedThirdParty,
    toggleThirdPartyRefreshList,
    addThirdPartyListConfigurationPreference,
    setThirdPartyListConfigurationPreference,
    toggleColumnThirdParty,
    updateThirdPartyList,
    updateThirdPartyListCriteria,
    updateThirdPartyListLoading,
    updateThirdPartyListCount,
    updateThirdPartyIsCreating,
    setThirdPartyColumns,
    setThirdParty,

    setCurrencies,
    setPaymentTypes,
    setPreferedPaymentType,
    setSalesStats,
    setEmptySalesReceipt,
} = salesReceipt.actions;